import { Component, ElementRef, HostListener, DOCUMENT, inject, signal, computed, effect, ViewChild } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { AppState } from '../../../core/store';
import { UiActions } from '../../../core/store/ui/ui.actions';
import { selectPricingEnableWorkflow, selectTheme, selectTelaVisibility, selectDarkVariant, selectSearchEnabled, selectShowEaProducts, selectShowUnavailableButton } from '../../../core/store/ui/ui.selectors';
import { selectIsSignedIn, selectIdToken, selectUser } from '../../../core/store/auth/auth.selectors';
import { Icon } from '../icon/icon';
import { Button } from '../button/button';
import { MarketplaceAuthService } from '../../../core/services/marketplace-auth.service';
import { SearchPanel } from '../search-panel/search-panel';
import { AuthActions } from '../../../core/store/auth/auth.actions';
import { environment } from '../../../../environments/environment';
import { AccountSelector } from '../account-selector/account-selector';
import { GoogleAnalyticsService } from '../../../core/services/google-analytics.service';

@Component({
  selector: 'app-nav',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, FormsModule, Icon, SearchPanel, Button, AccountSelector],
  templateUrl: './nav.html',
  styleUrl: './nav.scss',
})
export class Nav {
  @ViewChild('signinWrapper') private readonly signinWrapper!: ElementRef;
  @ViewChild('devWrapper') private readonly devWrapper: ElementRef | undefined;
  @ViewChild(SearchPanel) private readonly searchPanel!: SearchPanel;
  private store = inject(Store<AppState>);
  private document = inject(DOCUMENT);
  private el = inject(ElementRef);
  private readonly marketplaceAuthService = inject(MarketplaceAuthService);
  private readonly ga = inject(GoogleAnalyticsService);

  theme = toSignal(this.store.select(selectTheme), { initialValue: 'light' as 'light' | 'dark' | 'slb' });
  darkVariant = toSignal(this.store.select(selectDarkVariant), { initialValue: '1' as '1' | '2' });
  telaVisibility = toSignal(this.store.select(selectTelaVisibility), { initialValue: 'none' as 'none' | 'catalog' | 'all' });
  pricingEnableWorkflow = toSignal(this.store.select(selectPricingEnableWorkflow), { initialValue: false });
  searchEnabled = toSignal(this.store.select(selectSearchEnabled), { initialValue: false });
  showEaProducts = toSignal(this.store.select(selectShowEaProducts), { initialValue: false });
  showUnavailableButton = toSignal(this.store.select(selectShowUnavailableButton), { initialValue: false });
  isSignedIn = toSignal(this.store.select(selectIsSignedIn), { initialValue: false });
  user       = toSignal(this.store.select(selectUser),       { initialValue: null });
  private readonly idToken = toSignal(this.store.select(selectIdToken), { initialValue: null });
  themeSelectValue = computed<'light' | 'dark' | 'dark2'>(() => {
    if (this.theme() !== 'dark') return 'light';
    return this.darkVariant() === '2' ? 'dark2' : 'dark';
  });

  readonly showDevSettings = !environment.production;

  isScrolled = signal(false);
  activeSection = signal<string | null>(null);
  userMenuOpen = signal(false);
  devMenuOpen = signal(false);
  mobileNavOpen = signal(false);

  constructor() {
    effect(() => {
      const html = this.document.documentElement;
      if (this.theme() === 'dark') {
        html.setAttribute('data-theme', 'dark');
        if (this.darkVariant() === '2') {
          html.setAttribute('data-dark-variant', '2');
        } else {
          html.removeAttribute('data-dark-variant');
        }
      } else if (this.theme() === 'slb') {
        html.setAttribute('data-theme', 'slb');
        html.removeAttribute('data-dark-variant');
      } else {
        html.removeAttribute('data-theme');
        html.removeAttribute('data-dark-variant');
      }
    });

    this.document.documentElement.setAttribute('data-nav-variant', 'color');
  }

  @HostListener('document:click', ['$event.target'])
  onDocumentClick(target: EventTarget | null): void {
    if (this.userMenuOpen() && !this.signinWrapper?.nativeElement.contains(target)) {
      this.userMenuOpen.set(false);
    }
    if (this.devMenuOpen() && !this.devWrapper?.nativeElement.contains(target)) {
      this.devMenuOpen.set(false);
    }
    if (this.mobileNavOpen() && !this.el.nativeElement.contains(target)) {
      this.mobileNavOpen.set(false);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.userMenuOpen()) this.userMenuOpen.set(false);
    if (this.devMenuOpen()) this.devMenuOpen.set(false);
    if (this.mobileNavOpen()) this.mobileNavOpen.set(false);
  }

  @HostListener('window:scroll')
  onScroll(): void {
    this.isScrolled.set(window.scrollY > 10);
    this.updateScrollSpy();
  }

  private updateScrollSpy(): void {
    const sections = ['platform', 'spotlight', 'domain', 'features'];
    const threshold = window.innerHeight * 0.4;
    let active: string | null = null;
    for (const id of sections) {
      const el = this.document.getElementById(id);
      if (el && el.getBoundingClientRect().top <= threshold) active = id;
    }
    this.activeSection.set(active);
  }

  onThemeSelectChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as 'light' | 'dark' | 'dark2' | 'slb';
    if (value === 'light') {
      this.store.dispatch(UiActions.setTheme({ theme: 'light' }));
    } else if (value === 'slb') {
      this.store.dispatch(UiActions.setTheme({ theme: 'slb' }));
      this.store.dispatch(UiActions.setDarkVariant({ variant: '1' }));
    } else if (value === 'dark') {
      this.store.dispatch(UiActions.setTheme({ theme: 'dark' }));
      this.store.dispatch(UiActions.setDarkVariant({ variant: '1' }));
    } else {
      this.store.dispatch(UiActions.setTheme({ theme: 'dark' }));
      this.store.dispatch(UiActions.setDarkVariant({ variant: '2' }));
    }
  }

  setTelaVisibility(vis: 'none' | 'catalog' | 'all'): void {
    this.store.dispatch(UiActions.setTelaVisibility({ telaVisibility: vis }));
  }

  onTelaSelectChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as 'none' | 'catalog' | 'all';
    this.setTelaVisibility(value);
  }
  
  toggleSearch(): void {
    this.store.dispatch(UiActions.setSearchEnabled({ enabled: !this.searchEnabled() }));
  }

  togglePricingEnableWorkflow(): void {
    this.store.dispatch(UiActions.setPricingEnableWorkflow({ enabled: !this.pricingEnableWorkflow() }));
  }

  toggleShowEaProducts(): void {
    this.store.dispatch(UiActions.setShowEaProducts({ enabled: !this.showEaProducts() }));
  }

  toggleShowUnavailableButton(): void {
    this.store.dispatch(UiActions.setShowUnavailableButton({ enabled: !this.showUnavailableButton() }));
  }

  openUserMenu(): void {
    this.devMenuOpen.set(false);
    this.userMenuOpen.set(true);
  }

  closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  openDevMenu(): void {
    this.userMenuOpen.set(false);
    this.devMenuOpen.set(true);
  }

  closeDevMenu(): void {
    this.devMenuOpen.set(false);
  }

  toggleMobileNav(): void {
    this.mobileNavOpen.update((v) => !v);
  }

  signIn(): void {
    this.ga.trackEvent('sign_in_click', { location: 'nav' });
    this.marketplaceAuthService.login();
  }

  signOut(): void {
    this.userMenuOpen.set(false);
    const idToken = this.idToken(); // capture before dispatch nulls the store state
    this.store.dispatch(AuthActions.signOut());
    this.marketplaceAuthService.logout(
      idToken,
      globalThis.location.pathname + globalThis.location.search,
    );
  }

  openSearch(): void {
    this.userMenuOpen.set(false);
    this.searchPanel.open();
  }
}
